import React, { useCallback } from 'react';
import type { AnalysisSubject, CustomBlockTypeEnum } from 'venn-utils';
import { plural, Routes } from 'venn-utils';
import { ColorUtils, GetColor, Icon } from 'venn-ui-kit';
import styled from 'styled-components';
import type { GenericSearchMenuItem } from '../search-menu';
import { MultiSelectSearch, SearchMenuColumn } from '../search-menu';

import type { SelectedSubjectsProps } from './SelectedSubjects';
import SelectedSubjects from './SelectedSubjects';
import type { SetterOrUpdater } from 'recoil';
import type { Subject } from 'venn-state';

export const SUPPORT_30_OBJECTS: CustomBlockTypeEnum[] = [
  'FACTOR_CONTRIBUTION',
  'FACTOR_EXPOSURES',
  'PORTFOLIO_CONTRIBUTION',
  'PERFORMANCE_SUMMARY',
];

const MAX_SUBJECT_SIZE = 10;

export type SubjectListProps = {
  /** Indicate that only first item used */
  excludedSubjects?: number;
  portfoliosOnly?: boolean;
  readonly?: boolean;
  setSubjects: SetterOrUpdater<Subject[]>;
} & Pick<SelectedSubjectsProps, 'hideAllocatorLauncher' | 'hideItemTypePill' | 'maxSubjects' | 'subjects'>;

const location = window.location.pathname === Routes.REPORT_LAB_PATH ? 'reportLabModal' : 'builderSideBar';

const SubjectList = ({
  readonly,
  portfoliosOnly,
  setSubjects,
  subjects,
  maxSubjects,
  ...passThroughProps
}: SubjectListProps) => {
  const onSelectItems = useCallback(
    (items: GenericSearchMenuItem<AnalysisSubject>[]) =>
      setSubjects((current) => [
        ...current,
        ...items.map(({ value }) => ({ portfolioId: value?.portfolio?.id, fundId: value?.fund?.id })),
      ]),
    [setSubjects],
  );

  return (
    <>
      {!readonly && (
        <>
          <StyledSearchBar
            onSelected={onSelectItems}
            location={location}
            excludedItems={subjects.map((subject) => ({
              id: `${subject.fundId ?? subject.portfolioId ?? subject.privateFundId ?? subject.privatePortfolioId}`,
              type: subject.portfolioId ?? subject.privatePortfolioId ? 'PORTFOLIO' : 'FUND',
            }))}
            portfoliosOnly={portfoliosOnly}
            autofocus={false}
            blurOnSelect
            fixedMenuWidth
            usePortal
            maxSelections={maxSubjects - subjects.length + 1}
            menuTrailingContent={(selectionsLeft) =>
              selectionsLeft && subjects.length > MAX_SUBJECT_SIZE ? MaxSubjectWarning : undefined
            }
            scrollAddIntoView
            menuStyles={{
              minWidth: '600px',
              right: 0,
              position: 'absolute',
            }}
            columns={[SearchMenuColumn.CCY, SearchMenuColumn.IDENTIFIER, SearchMenuColumn.AVAILABLE_DATES]}
            privateAssetSearchMode="PUBLIC_ONLY"
          />
          <ClearWrapper>
            {plural(subjects.length, { 1: '{{count}} Object', other: '{{count}} Objects' })} Added
            <LinkText data-testid="qa-clear-subject-list" onClick={() => setSubjects([])}>
              Clear
            </LinkText>
          </ClearWrapper>
        </>
      )}
      <SelectedSubjects
        subjects={subjects}
        setSubjects={setSubjects}
        reorderable={!readonly}
        maxSubjects={maxSubjects}
        {...passThroughProps}
      />
    </>
  );
};

export default SubjectList;

const StyledSearchBar = styled(MultiSelectSearch)`
  .select__control {
    transition: width 0.5s ease-in-out;
    width: 100%;
    height: auto;
  }
`;

const WarningBar = styled.div`
  height: 35px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  font-size: 10px;
  background-color: ${ColorUtils.hex2rgbaFrom(GetColor.HighlightLight, 0.1)};
  border-top: 1px solid ${GetColor.Grey};
  border-bottom: 1px solid ${GetColor.Grey};
`;

const StyledIcon = styled(Icon)`
  font-size: 14px;
  margin-right: 10px;
  color: ${GetColor.HighlightDark};
`;

const MaxSubjectWarning = (
  <WarningBar>
    <StyledIcon prefix="far" type="exclamation-circle" />
    Adding more than 10 objects may impact system responsiveness.
  </WarningBar>
);

const ClearWrapper = styled.div`
  margin: 10px 0 5px;
  font-size: 12px;
  display: flex;
`;

const LinkText = styled.button`
  color: ${GetColor.Primary.Dark};
  font-weight: bold;
  padding-left: 5px;
  margin-left: 5px;
  border-left: 1px solid ${GetColor.GreyScale.Grey60};
`;
